import { ActionIcon, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import type { FlowRules } from '@dealstack/shared/lib/common/business/documentFlow';
import type { DataPointWithSampleRes } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';

import { describePredicate } from './rules.utils';
import type { RuleCohort } from './rules.types';

interface Props {
	rules: FlowRules;
	dataPoints: DataPointWithSampleRes[];
	cohorts: RuleCohort[];
	/** How many blocks each rule marks, so an edit can say what it would move. */
	ruleUsage: ReadonlyMap<string, number>;
	onAdd: () => void;
	onEdit: (ruleId: string) => void;
	/** Marks whatever is selected in the document with this rule, so one rule covers many places. */
	onApplyToSelection: (ruleId: string) => void;
	onRemove: (ruleId: string) => void;
}

const usedIn = (count: number): string => {
	if (count === 0) {
		return 'Nothing is marked with this rule';
	}
	return count === 1 ? 'Used in 1 place' : `Used in ${count} places`;
};

/**
 * Every rule in the document, in one place.
 *
 * A rule is reusable by design — the document holds it once and any number of blocks point at
 * it — so the card carries the two actions that make that real: apply it somewhere else, and
 * edit it. The usage count is not decoration: editing a rule used in six places changes all six
 * at once, including the ones not on screen, and the author has to be told that before they open
 * the editor rather than after they save.
 */
export const RulesPanel = ({
	rules,
	dataPoints,
	cohorts,
	ruleUsage,
	onAdd,
	onEdit,
	onApplyToSelection,
	onRemove,
}: Props) => {
	const entries = Object.entries(rules);

	return (
		<Stack gap="sm" data-testid="rules-panel">
			<Button size="xs" variant="light" onClick={onAdd} data-testid="rules-panel-add">
				+ New rule
			</Button>

			{entries.length === 0 && (
				<Text size="sm" c="dark.0">
					No rules yet. Select the content that should only appear sometimes, then press
					<b> If… </b> in the toolbar.
				</Text>
			)}

			{entries.map(([ruleId, rule]) => {
				const count = ruleUsage.get(ruleId) ?? 0;
				return (
					<Stack
						key={ruleId}
						gap={6}
						p="sm"
						style={{ border: '1px solid #dee2e6', borderRadius: 8 }}
						data-testid={`rule-${ruleId}`}
					>
						<Group justify="space-between" wrap="nowrap">
							<Text size="sm" fw={600}>
								{rule.label}
							</Text>
							<Group gap={2} wrap="nowrap">
								<ActionIcon
									variant="subtle"
									aria-label={`Edit rule ${rule.label}`}
									onClick={() => onEdit(ruleId)}
									data-testid={`rule-edit-${ruleId}`}
								>
									<IconPencil size={16} />
								</ActionIcon>
								<ActionIcon
									variant="subtle"
									color="red"
									aria-label={`Delete rule ${rule.label}`}
									data-testid={`rule-delete-${ruleId}`}
									onClick={() => onRemove(ruleId)}
								>
									<IconTrash size={16} />
								</ActionIcon>
							</Group>
						</Group>

						<Text size="xs" c="dark.0">
							Shows only when {describePredicate(rule.when, dataPoints, cohorts)}.
							Everyone else does not see it.
						</Text>

						<Group gap={6} wrap="nowrap">
							<Badge size="xs" variant="light" color={count === 0 ? 'red' : 'gray'}>
								{usedIn(count)}
							</Badge>
						</Group>

						<Button
							size="compact-xs"
							variant="default"
							onClick={() => onApplyToSelection(ruleId)}
							data-testid={`rule-apply-${ruleId}`}
						>
							Also apply to what I have selected
						</Button>
					</Stack>
				);
			})}
		</Stack>
	);
};
